/**
 * @file Client entrypoint for the `@labcraft/lab` SPA. Constructs the
 *       frontend `Lab` Client + `HttpClientTransport` +
 *       `WsClientTransport` + `QueryClient`, hands them to
 *       `<ClientProvider>`, and mounts the router.
 * @module @labcraft/lab/ui/main
 *
 * Bootstrap order: nothing inline — the lab SPA doesn't carry a
 * pre-paint theme switcher (yet). Vite's `/api` proxy forwards to the
 * lab daemon in dev; Caddy on the production host does the same.
 *
 * Phase 11a of umbrella #406: the frontend operator. Constructs the
 * two transports + the query cache + the typed Client here, then
 * hands them to `<ClientProvider>` which drives `configure → start`
 * on mount and `stop` on unmount. The lifecycle gating lives in the
 * provider; `main.tsx` is just composition.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as JsxRuntime from 'react/jsx-runtime'
import * as MdxReact from '@mdx-js/react'
import { QueryClient } from '@tanstack/react-query'
import {
  BrowserAcquisition,
  HttpClientTransport,
  WsClientTransport,
} from '@labcraft/core/frontend'
import { ClientProvider } from '@labcraft/core/frontend/react'
import { Lab } from '../client/index.ts'
import { AppRouter } from './routes/index.tsx'
import { qkMe } from './hooks/query_keys.ts'
import { installLearnerEvents } from './learner_events.ts'
import { forwardEngagementAnalytics } from './engagement_analytics.ts'
import { applyStoredReduceMotion } from './lib/reduce_motion.ts'
import '@labcraft/ui/global.css'

////////////////////////////////////////////////////////////////////////////////
// Chapter-satellite runtime exposure
////////////////////////////////////////////////////////////////////////////////

// Course-kit's builder rewrites `import { jsx } from "react/jsx-runtime"`
// and `import { useMDXComponents } from "@mdx-js/react"` in compiled
// chapter modules into `const { jsx } = globalThis.__lc_jsxRuntime` /
// `const { useMDXComponents } = globalThis.__lc_mdxReact`. The
// satellite must read the SAME React instance the SPA renders against —
// bundling React into the satellite gives it its own context registry
// and `useContext` returns null inside MDX. Expose the host's React
// runtime here, before any chapter mounts.
;(
  globalThis as unknown as {
    __lc_jsxRuntime: typeof JsxRuntime
    __lc_mdxReact: typeof MdxReact
  }
).__lc_jsxRuntime = JsxRuntime
;(
  globalThis as unknown as {
    __lc_jsxRuntime: typeof JsxRuntime
    __lc_mdxReact: typeof MdxReact
  }
).__lc_mdxReact = MdxReact

////////////////////////////////////////////////////////////////////////////////
// Boot
////////////////////////////////////////////////////////////////////////////////

const root = document.getElementById('root')
if (!root) throw new Error('#root element missing from index.html')

// Apply the persisted reduce-motion preference to `<html>` before React
// mounts so an opted-in learner never sees a flash of animated chrome on
// reload. The Settings modal toggles this live; this is the boot read.
applyStoredReduceMotion()

// First-touch acquisition capture. `/authorize` is lab's only
// unauthenticated surface, so a live classification at sign-in already
// works here — but capturing first-touch to localStorage on boot keeps lab
// symmetric with www (one shared `BrowserAcquisition` in core/frontend) and
// future-proofs any anonymous multi-page flow lab might grow. Write-once,
// best-effort; the sign-in client reads the same store.
BrowserAcquisition.captureFirstTouch()

// Same-origin. Route paths are wire-true (each route's `.path` is the
// full URL from origin, e.g. `/api/account/me`), so baseUrl is just
// the origin — no `/api` segment. In dev, Vite's `/api` proxy
// forwards path-prefixed requests to the daemon; in prod, Caddy
// proxies `/api/*` likewise.
const http = new HttpClientTransport({ baseUrl: window.location.origin })

// `WsClientTransport` auto-rewrites `http(s)://` to `ws(s)://`.
// `lab.chapter` opens per-machine terminal subscriptions through this
// transport on `attachTerminals(machines)`.
const ws = new WsClientTransport({ baseUrl: window.location.origin })

// One TanStack cache per page load.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Account state is rarely contested; 30s staleness keeps the
      // chrome responsive on tab-flip without stampeding the daemon
      // on every render.
      staleTime: 30_000,
      // One retry hides a transient network blip; more retries on a
      // 401 prolong the wrong-state UI.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// 401 from any authed call → flip `qkMe` to `{ learner: null }`
// synchronously so the chrome's `useMe()` reader bounces on the next
// render. `setQueryData` (vs `invalidateQueries`) is load-bearing —
// invalidation would mark stale and refetch on the next mount,
// leaving one render with the signed-in chrome around an anonymous
// cache. Wiring lives here (the UI composition layer) rather than on
// the Client itself so `Lab` stays transport-only and React-free.
// `SessionExpiredWatcher` observes the cache transition downstream
// and bounces to `/authorize?expired=1&next=…`.
http.on('unauthorized', () => {
  queryClient.setQueryData(qkMe, { learner: null })
})

const lab = new Lab({ id: 'lab' }, { http, ws })

// Install the typed `LearnerEvents` bus on `window` so Playwright
// PersonaSession specs can `addInitScript` a tap that records every
// UI-observable transition. SPA components (TerminalPane, etc.) emit
// onto it at the post-mount commit boundary — see #701.
const learnerEvents = installLearnerEvents()

// Tap the bus's engagement-lifecycle transitions (suspend / resume /
// expired) onto Umami as the locked `engagement_paused` /
// `engagement_resumed` / `engagement_expired` events (#1216). Idempotent
// — the forwarder guards on a bus flag so StrictMode double-invocation
// never double-registers.
forwardEngagementAnalytics(learnerEvents)

createRoot(root).render(
  <StrictMode>
    <ClientProvider client={lab} queryClient={queryClient}>
      <AppRouter />
    </ClientProvider>
  </StrictMode>,
)
